import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../Button';
import { googleApiKey } from '../../../constants';
import { getStaticMapRequest } from '../../../helpers/getMapViewport';

const STATIC_MAP_ENDPOINT = 'https://maps.googleapis.com/maps/api/staticmap';

const buildStaticMapUrl = ({ center, zoom, width, height, scale }) => {
  const params = new URLSearchParams({
    center: `${center.lat},${center.lng}`,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: String(scale),
    key: googleApiKey,
  });

  return `${STATIC_MAP_ENDPOINT}?${params.toString()}`;
};

/**
 * Stands in for the dynamic map until someone asks for it: a Dynamic Maps load is billed
 * every time a map is instantiated, whether or not anyone touches it.
 *
 * The image shows the area, not the results, so it is requested once and left alone when
 * the results are filtered or the window is resized. Every new url would be another billed
 * Maps Static API request.
 */
const StaticMapPreview = ({ initialViewport, initialWidth, initialHeight, onActivate }) => {
  const [staticMapUrl] = useState(() =>
    buildStaticMapUrl(
      getStaticMapRequest({ ...initialViewport, width: initialWidth, height: initialHeight }),
    ),
  );

  return (
    <div
      className="relative h-full w-full cursor-pointer overflow-hidden"
      onClick={onActivate}
      onKeyDown={(event) => {
        // Enter on the focused button already produces a click that bubbles to this
        // handler's element, so only act on Enter aimed at the wrapper itself.
        if (event.key === 'Enter' && event.target === event.currentTarget) {
          onActivate();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Show interactive map"
    >
      <img
        src={staticMapUrl}
        alt="Map of the search area"
        className="h-full w-full object-cover"
        width={initialWidth}
        height={initialHeight}
      />

      {/* The call to action carries no handler of its own: the click bubbles to the
          wrapper, which is also what catches a click anywhere else on the image. Giving
          the button its own onClick as well would activate, and report, twice. */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Button
          variant={Button.buttonVariants.PRIMARY}
          size={Button.buttonSizes.SM}
          className="pointer-events-auto shadow-lg"
        >
          Show on map
        </Button>
      </div>
    </div>
  );
};

StaticMapPreview.propTypes = {
  initialViewport: PropTypes.shape({
    center: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
    zoom: PropTypes.number,
  }).isRequired,
  initialWidth: PropTypes.number.isRequired,
  initialHeight: PropTypes.number.isRequired,
  onActivate: PropTypes.func.isRequired,
};

export default React.memo(StaticMapPreview);
